import {zodResolver} from '@hookform/resolvers/zod';
import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {useConfirm} from '@/components/confirm-dialog/useConfirm';
import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import {FormSkeleton} from '@/components/ui/skeleton';
import {useToast} from '@/components/ui/use-toast';
import {useAuthorize} from '@/hooks/useAuth';
import {graphqlClient} from '@/services/graphqlClient';
import type {RecurringTask} from '@/services/graphqlHooks';
import {
  useDeleteRecurringTaskMutation,
  useGetRecurringTaskQuery,
  useUpdateRecurringTaskMutation,
} from '@/services/graphqlHooks';

import type {
  TaskFormValues,
  TaskFromDefaultValueInput,
} from '../../utils/tasksFormInputs';
import {
  mapTaskFormValuesToRecurringTaskInput,
  StandardTaskVariant,
  taskFormDefaultValues,
  taskFormSchema,
} from '../../utils/tasksFormInputs';
import TaskFormFields from '../TaskFormFields';

export type EditRecurringTaskDialogProps = DialogProps;

const EditRecurringTaskDialog = ({
  onOpenChange,
  ...props
}: EditRecurringTaskDialogProps) => {
  const {t} = useTranslation();
  const {confirm} = useConfirm();
  const {toast} = useToast();

  const canUpdateTask = useAuthorize('UPDATE_TASK');

  const [searchParams] = useSearchParams();
  const id = searchParams.get('recurringTask') || '';

  const deleteTask = useDeleteRecurringTaskMutation(graphqlClient);
  const updateTask = useUpdateRecurringTaskMutation(graphqlClient);

  const isDisabled = updateTask.isPending || deleteTask.isPending;
  const {data: taskData, isLoading: isTaskLoading} = useGetRecurringTaskQuery(
    graphqlClient,
    {id},
    {
      enabled: !!(id && props.open),
      staleTime: 0,
      refetchOnMount: 'always',
      initialData: undefined,
    }
  );

  const task = taskData?.getRecurringTask as RecurringTask;

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      ...taskFormDefaultValues(task as TaskFromDefaultValueInput),
      standardTaskVariant: StandardTaskVariant.Recurring,
    },
  });

  useEffect(() => {
    if (!task) return;
    form.reset({
      ...taskFormDefaultValues(task as TaskFromDefaultValueInput),
      standardTaskVariant: StandardTaskVariant.Recurring,
    });
    form.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);

  const handleSubmit = async (values: TaskFormValues) => {
    try {
      await updateTask.mutateAsync({
        input: {id, ...mapTaskFormValuesToRecurringTaskInput(values)},
      });
      toast({
        title: t('Task updated'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      await confirm({
        title: t('Delete task'),
        message: t('Are you sure you want to delete this task?'),
      });
      await deleteTask.mutateAsync({id});
      toast({
        title: t('Task deleted'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={onOpenChange}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader className="md:hidden">
              <DialogTitle>{t('Task')}</DialogTitle>
            </DialogHeader>
            <DialogSection className="py-0">
              {isTaskLoading && <FormSkeleton />}
              {!isTaskLoading && !task && <div>{t('Task not found')}</div>}

              {canUpdateTask && task && (
                <TaskFormFields form={form} hideTaskType isStartDateLocked />
              )}
              {!canUpdateTask && task && <div>{t('Preview task')}</div>}
            </DialogSection>
            <DialogFooter>
              {!!task && canUpdateTask && (
                <>
                  <Button
                    type="button"
                    className="sm:mr-auto"
                    variant="warning"
                    onClick={() => handleDelete()}
                    disabled={isDisabled}
                  >
                    {t('Delete recurring task')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => onOpenChange?.(false)}
                  >
                    {t('Close')}
                  </Button>
                  <Button variant="primary" type="submit" disabled={isDisabled}>
                    {t('Submit')}
                  </Button>
                </>
              )}
              {!task && (
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => onOpenChange?.(false)}
                >
                  {t('Close')}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default EditRecurringTaskDialog;
